(() => {
  'use strict';
  if (window.BuyonikSavedCart) return;
  const node = document.getElementById('by-saved-cart-config');
  if (!node) return;
  const cfg = JSON.parse(node.textContent), t = cfg.text;
  if (cfg.designMode || location.origin !== new URL(cfg.redirectUri).origin) return;
  const nativeFetch = window.fetch.bind(window);
  const prefix = 'buyonik:cart:v1:', ownerKey = prefix + 'owner';
  const query = `query BuyonikSavedCart { customer { id metafield(namespace: "buyonik", key: "saved_cart") { value compareDigest } } }`;
  const mutation = `mutation BuyonikSaveCart($metafields: [MetafieldsSetInput!]!) { metafieldsSet(metafields: $metafields) { metafields { value compareDigest } userErrors { code field message } } }`;
  let auth, discovery, customer, digest = null, savedFingerprint = '', active = false, leaving = false;
  let paused = false, timer, queue = Promise.resolve(), initializing = true, navigating = false, status = {message: t.connecting};
  const requests = new Set();
  const revisionAttribute = '__buyonik_cart_revision';
  const approvedCheckoutForms = new WeakSet();
  let currentSaved = null, checkedAt = 0, taggedRevision = null;
  let deferredItems = [], restoreFailed = false, cartRestored = false;
  const productNames = new Map();
  const root = cfg.root.endsWith('/') ? cfg.root : cfg.root + '/';
  const read = (storage, key) => { const value = storage.getItem(prefix + key); return value ? JSON.parse(value) : null; };
  const put = (storage, key, value) => value === null ? storage.removeItem(prefix + key) : storage.setItem(prefix + key, JSON.stringify(value));
  const fail = key => new Error(t[key] || t.error);
  function setStatus(message, action, loud = false) {
    status = {message, action, loud};
    document.querySelectorAll('[data-by-saved-cart-status]').forEach(box => {
      box.replaceChildren();
      const span = document.createElement('span'); span.textContent = message; box.append(span);
      if (action === 'retry-stock' && !restoreFailed && deferredItems.length) {
        const explanation = document.createElement('p'); explanation.textContent = t.stock_message; box.append(explanation);
        const list = document.createElement('ul');
        deferredItems.forEach(item => {
          const row = document.createElement('li');
          const name = document.createElement('strong'); name.textContent = productNames.get(item.id) || item.title || t.unknown_item;
          const quantity = document.createElement('span'); quantity.textContent = t.stock_quantity.replace('[quantity]', String(item.quantity));
          row.append(name, document.createElement('br'), quantity); list.append(row);
        });
        box.append(list);
      }
      if (action) {
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.bySavedAction = action;
        button.textContent = action === 'connect' ? t.connect : action === 'keep' ? t.keep : action === 'retry-stock' && !restoreFailed ? t.stock_retry : t.retry;
        box.append(' ', button);
      }
    });
    const notice = document.querySelector('[data-by-saved-cart-notice]');
    if (notice) notice.hidden = !loud;
  }
  async function jsonFetch(url, options = {}) {
    const response = await nativeFetch(url, {cache: 'no-store', signal: AbortSignal.timeout(15000), ...options});
    let data;
    try { data = await response.json(); } catch { throw fail('error'); }
    if (!response.ok) throw Object.assign(fail('error'), {status: response.status, details: data});
    return data;
  }
  function cartRequest(path = 'cart.js', body) {
    return jsonFetch(root + path, {credentials: 'same-origin', ...(body ? {
      method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)
    } : {})});
  }
  function properties(source) {
    return Object.fromEntries(Object.entries(source || {}).filter(([key, value]) =>
      key !== '__proto__' && key !== 'constructor' && typeof value === 'string').sort(([a], [b]) => a.localeCompare(b)));
  }
  function snapshot(cart) {
    return {version: 1, items: cart.items.map(item => ({
      id: String(item.variant_id), quantity: item.quantity, properties: properties(item.properties),
      ...(item.title ? {title: String(item.title).slice(0, 500)} : {}),
      ...(item.selling_plan_allocation?.selling_plan?.id ? {selling_plan: String(item.selling_plan_allocation.selling_plan.id)} : {})
    })), note: cart.note || ''};
  }
  const lineKey = item => JSON.stringify([item.id, item.selling_plan || '', properties(item.properties)]);
  const fingerprint = value => JSON.stringify({items: value.items.map(item => ({id: item.id, quantity: item.quantity, properties: properties(item.properties), ...(item.selling_plan ? {selling_plan: item.selling_plan} : {})})).sort((a, b) => lineKey(a).localeCompare(lineKey(b))), note: value.note || ''});
  const deferredFingerprint = value => fingerprint({items: value || [], note: ''});
  function mergeLines(items) {
    const lines = new Map();
    for (const item of items) {
      const key = lineKey(item), prior = lines.get(key);
      lines.set(key, {...item, quantity: item.quantity + (prior?.quantity || 0), title: item.title || prior?.title || ''});
    }
    return [...lines.values()];
  }
  function savedStatus() {
    if (deferredItems.length) setStatus(t.stock_heading, 'retry-stock');
    else setStatus(t.saved);
  }
  async function nameDeferredItems() {
    const ids = [...new Set(deferredItems.map(item => item.id))].filter(id => !productNames.has(id));
    if (ids.length) {
      try {
        const result = await jsonFetch('/api/2026-10/graphql.json', {method: 'POST', credentials: 'omit', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({query: `query BuyonikSavedProductNames($ids:[ID!]!,$language:LanguageCode!) @inContext(language:$language){nodes(ids:$ids){... on ProductVariant{id title product{title}}}}`,
            variables: {ids: ids.map(id => 'gid://shopify/ProductVariant/' + id), language: cfg.locale.startsWith('en') ? 'EN' : 'FR'}})});
        for (const variant of result.data?.nodes || []) {
          if (variant?.product?.title) productNames.set(variant.id.split('/').pop(), variant.product.title + (variant.title && variant.title !== 'Default Title' ? ' — ' + variant.title : ''));
        }
      } catch {}
    }
    deferredItems = deferredItems.map(item => ({...item, ...(productNames.get(item.id) ? {title: productNames.get(item.id)} : {})}));
  }
  function decodeSaved(value) {
    if (!value) return null;
    let parsed;
    try { parsed = JSON.parse(value); } catch { throw fail('error'); }
    if (parsed.version !== 1 || !Array.isArray(parsed.items) || parsed.items.length > 250) throw fail('error');
    if (parsed.deferredItems !== undefined && (!Array.isArray(parsed.deferredItems) || parsed.deferredItems.length > 250)) throw fail('error');
    const decodeItems = items => items.map(item => {
      if (!/^\d+$/.test(String(item.id)) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 100000) throw fail('error');
      if (item.selling_plan && !/^\d+$/.test(String(item.selling_plan))) throw fail('error');
      return {id: String(item.id), quantity: item.quantity, properties: properties(item.properties), ...(typeof item.title === 'string' ? {title: item.title.slice(0, 500)} : {}), ...(item.selling_plan ? {selling_plan: String(item.selling_plan)} : {})};
    });
    return {version: 1, items: decodeItems(parsed.items), deferredItems: decodeItems(parsed.deferredItems || []), note: typeof parsed.note === 'string' ? parsed.note : '',
      revision: typeof parsed.revision === 'string' && /^[A-Za-z0-9_-]{43}$/.test(parsed.revision) ? parsed.revision : '',
      revisionAt: Number.isSafeInteger(parsed.revisionAt) ? parsed.revisionAt : 0};
  }
  async function discover() {
    if (!discovery) discovery = Promise.all([
      jsonFetch('/.well-known/openid-configuration'), jsonFetch('/.well-known/customer-account-api')
    ]).then(([oidc, api]) => ({...oidc, graphql_api: api.graphql_api.replace(/\/api\/\d{4}-\d{2}\/graphql$/, '/api/2026-10/graphql')}));
    return discovery;
  }
  const b64 = bytes => btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const random = () => b64(crypto.getRandomValues(new Uint8Array(32)));
  function targetPath(path = location.href) {
    const target = new URL(path, location.origin);
    if (target.origin !== location.origin) return root;
    if (cfg.preview) target.searchParams.set('preview_theme_id', cfg.themeId);
    return target.pathname + target.search + target.hash;
  }
  async function authorize(interactive = false, intent = 'sync') {
    setStatus(t.connecting);
    window.BuyonikCartTransition?.show(t.connecting);
    const config = await discover(), verifier = random(), state = 'bycart_' + random(), nonce = random();
    const pending = {verifier, state, nonce, created: Date.now(), expected: cfg.customerId, returnTo: targetPath(), intent};
    put(sessionStorage, 'pending', pending);
    put(sessionStorage, 'attempted', cfg.customerId);
    const url = new URL(config.authorization_endpoint);
    const challenge = b64(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
    Object.entries({client_id: cfg.clientId, scope: 'openid email customer-account-api:full', response_type: 'code',
      redirect_uri: cfg.redirectUri, state, nonce, code_challenge: challenge, code_challenge_method: 'S256',
      locale: cfg.locale.startsWith('en') ? 'en' : 'fr', region_country: 'CA', ...(!interactive ? {prompt: 'none'} : {})
    }).forEach(([key, value]) => url.searchParams.set(key, value));
    navigating = true; location.replace(url.href);
  }
  function tokenClaims(token) {
    try { return JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)))); }
    catch { throw fail('identity'); }
  }
  async function callback() {
    const result = window.__buyonikCartCallback;
    delete window.__buyonikCartCallback;
    if (!result) return null;
    const pending = read(sessionStorage, 'pending');
    put(sessionStorage, 'pending', null);
    if (!pending || pending.state !== result.state || Date.now() - pending.created > 600000 || pending.expected !== cfg.customerId) throw fail('identity');
    if (result.error || !result.code) throw fail('connect');
    const config = await discover();
    const tokens = await jsonFetch(config.token_endpoint, {method: 'POST', headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body: new URLSearchParams({grant_type: 'authorization_code', client_id: cfg.clientId, redirect_uri: cfg.redirectUri, code: result.code, code_verifier: pending.verifier})});
    const claims = tokenClaims(tokens.id_token);
    const audiences = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
    if (claims.nonce !== pending.nonce || claims.iss !== config.issuer || !audiences.includes(cfg.clientId) || claims.exp * 1000 < Date.now()) throw fail('identity');
    auth = {...tokens, expiresAt: Date.now() + tokens.expires_in * 1000, customerId: pending.expected};
    put(sessionStorage, 'auth', auth);
    return pending;
  }
  async function accessToken() {
    if (!auth || auth.customerId !== cfg.customerId) throw fail('connect');
    if (auth.expiresAt > Date.now() + 30000) return auth.access_token;
    if (!auth.refresh_token) throw fail('connect');
    const config = await discover();
    const tokens = await jsonFetch(config.token_endpoint, {method: 'POST', headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body: new URLSearchParams({grant_type: 'refresh_token', client_id: cfg.clientId, refresh_token: auth.refresh_token})});
    auth = {...auth, ...tokens, expiresAt: Date.now() + tokens.expires_in * 1000};
    put(sessionStorage, 'auth', auth);
    return auth.access_token;
  }
  async function graphql(operation, variables = {}) {
    const token = await accessToken(), config = await discover();
    const result = await jsonFetch(config.graphql_api, {method: 'POST', headers: {'Content-Type': 'application/json', Authorization: token}, body: JSON.stringify({query: operation, variables})});
    if (result.errors?.length) throw fail('error');
    return result.data;
  }
  async function remoteCart() {
    const result = await graphql(query);
    if (!result.customer || result.customer.id !== 'gid://shopify/Customer/' + cfg.customerId) throw fail('identity');
    customer = result.customer.id;
    const field = result.customer.metafield;
    return {value: decodeSaved(field?.value), digest: field?.compareDigest || null};
  }
  const lock = work => navigator.locks ? navigator.locks.request(prefix + 'sync', work) : work();
  function serial(work) {
    const next = queue.then(() => lock(work));
    queue = next.catch(() => {});
    return next;
  }
  function remember(cart, remoteDigest = digest) {
    put(localStorage, 'owner', {id: cfg.customerId, digest: remoteDigest,
      revision: currentSaved?.revision || '', revisionAt: currentSaved?.revisionAt || 0,
      fingerprint: fingerprint(snapshot(cart))});
  }
  async function writeRemote(value) {
    if (!customer) throw fail('identity');
    setStatus(t.saving);
    const result = await graphql(mutation, {metafields: [{ownerId: customer, namespace: 'buyonik', key: 'saved_cart', type: 'json',
      value: JSON.stringify({...value, updatedAt: new Date().toISOString()}), compareDigest: digest}]});
    const errors = result.metafieldsSet.userErrors;
    if (errors.length) {
      if (errors.some(error => /DIGEST|STALE/.test(error.code))) { paused = true; setStatus(t.conflict, 'keep', true); }
      throw fail(paused ? 'conflict' : 'error');
    }
    digest = result.metafieldsSet.metafields[0].compareDigest;
    currentSaved = value;
    savedFingerprint = fingerprint(value);
  }
  async function persist(cart) {
    if (!active || paused || !customer) throw fail('error');
    const value = {...snapshot(cart), deferredItems}, mark = fingerprint(value);
    if (mark !== savedFingerprint || deferredFingerprint(deferredItems) !== deferredFingerprint(currentSaved?.deferredItems) || !currentSaved?.revision) {
      await writeRemote({...value, revision: random(), revisionAt: checkedAt});
    }
    put(localStorage, 'restore:' + cfg.customerId, null);
    const revision = value.items.length ? cfg.customerId + ':' + currentSaved.revision : '';
    if (taggedRevision !== revision) {
      await cartRequest('cart/update.js', {attributes: {[revisionAttribute]: revision}});
      taggedRevision = revision;
    }
    remember(cart);
    savedStatus();
  }
  async function completedRevisions(remote, owner) {
    const candidates = [remote.value, owner?.id === cfg.customerId ? owner : null].filter(value => value?.revision);
    const targets = new Set(candidates.map(value => cfg.customerId + ':' + value.revision));
    const found = new Set();
    const oldestRevision = Math.min(...candidates.map(value => value.revisionAt || 0));
    let page = 1;
    do {
      const url = new URL(root, location.origin);
      url.searchParams.set('view', 'by-cart-orders');
      url.searchParams.set('page', String(page));
      url.searchParams.set('_by_check', String(Date.now()));
      if (cfg.preview) url.searchParams.set('preview_theme_id', cfg.themeId);
      const data = await jsonFetch(url.href, {credentials: 'same-origin'});
      if (data.version !== 1 || data.customerId !== cfg.customerId || !Array.isArray(data.orders) ||
          !Number.isSafeInteger(data.checkedAt)) throw fail('identity');
      checkedAt = data.checkedAt;
      for (const order of data.orders) if (targets.has(order.revision)) found.add(order.revision);
      if (!targets.size || found.size === targets.size || !data.nextPage ||
          (data.oldestAt && data.oldestAt < oldestRevision - 60)) break;
      if (!Number.isSafeInteger(data.nextPage) || data.nextPage <= page) throw fail('error');
      page = data.nextPage;
    } while (true);
    return found;
  }
  async function restore(saved, cart) {
    const before = fingerprint(snapshot(cart));
    const targets = mergeLines([...saved.items, ...(saved.deferredItems || [])]);
    put(localStorage, 'restore:' + cfg.customerId, {digest, saved: {version: 1, items: targets, note: saved.note || ''}});
    const missingFrom = current => {
      const quantities = new Map(mergeLines(snapshot(current).items).map(item => [lineKey(item), item.quantity]));
      return targets.map(item => ({...item, quantity: Math.max(0, item.quantity - (quantities.get(lineKey(item)) || 0))})).filter(item => item.quantity);
    };
    const payload = items => items.map(({title, ...item}) => item);
    let missing = missingFrom(cart);
    if (missing.length) {
      setStatus(t.restoring);
      try {
        await cartRequest('cart/add.js', {items: payload(missing)});
      } catch (error) {
        if (![404, 422].includes(error.status)) throw error;
        cart = await cartRequest();
        if (missing.length > 1) {
          for (const item of missingFrom(cart)) {
            try { await cartRequest('cart/add.js', {items: payload([item])}); }
            catch (error) { if (![404, 422].includes(error.status)) throw error; }
            cart = await cartRequest();
          }
        }
      }
    }
    cart = await cartRequest();
    deferredItems = missingFrom(cart);
    if (!cart.note && saved.note) {
      await cartRequest('cart/update.js', {note: saved.note});
      cart = await cartRequest();
    }
    await nameDeferredItems();
    cartRestored = cartRestored || before !== fingerprint(snapshot(cart));
    return cart;
  }
  async function initializeAccount(retryStock = false) {
    let owner = read(localStorage, 'owner');
    const remote = await remoteCart();
    const completed = await completedRevisions(remote, owner);
    digest = remote.digest; currentSaved = remote.value;
    deferredItems = remote.value?.deferredItems || [];
    savedFingerprint = remote.value ? fingerprint(remote.value) : '';
    let cart = await cartRequest();
    if (owner?.id && owner.id !== cfg.customerId) {
      cart = await clearBrowserCart(cart);
      put(localStorage, 'owner', null); owner = null;
    }
    active = true;
    if (remote.value?.revision && completed.has(cfg.customerId + ':' + remote.value.revision)) {
      await writeRemote({version: 1, items: [], deferredItems, note: '', revision: random(), revisionAt: checkedAt});
      put(localStorage, 'restore:' + cfg.customerId, null);
    }
    if (owner?.revision && completed.has(cfg.customerId + ':' + owner.revision)) {
      if (!cart.items.length || owner.fingerprint === fingerprint(snapshot(cart))) {
        cart = await clearBrowserCart(cart);
        put(localStorage, 'owner', null); owner = null;
      } else {
        paused = true; setStatus(t.conflict, 'keep', true); return;
      }
    }
    const pendingRestore = read(localStorage, 'restore:' + cfg.customerId);
    if (pendingRestore && pendingRestore.digest !== digest) {
      paused = true; setStatus(t.conflict, 'keep', true); return;
    }
    if (pendingRestore) {
      try { cart = await restore(decodeSaved(JSON.stringify(pendingRestore.saved)), cart); }
      catch (error) { paused = true; restoreFailed = true; setStatus(t.restore_error, 'retry-stock', true); throw error; }
    } else if (!owner || owner.id !== cfg.customerId) {
      if (currentSaved) {
        try { cart = await restore(currentSaved, cart); }
        catch (error) { paused = true; restoreFailed = true; setStatus(t.restore_error, 'retry-stock', true); throw error; }
      }
    } else if (owner.digest !== digest && currentSaved && fingerprint(snapshot(cart)) !== savedFingerprint) {
      paused = true; setStatus(t.conflict, 'keep', true); return;
    }
    if (!pendingRestore && retryStock && owner?.id === cfg.customerId && deferredItems.length) {
      try { cart = await restore({...snapshot(cart), deferredItems}, cart); }
      catch (error) { paused = true; restoreFailed = true; setStatus(t.restore_error, 'retry-stock', true); throw error; }
    }
    await nameDeferredItems();
    await persist(cart);
  }
  async function retryStock() {
    await ready;
    if (leaving || (paused && !restoreFailed)) return;
    await Promise.allSettled([...requests]);
    await serial(async () => {
      paused = false; restoreFailed = false;
      await initializeAccount(true);
    });
    if (cartRestored && /\/cart\/?$/.test(location.pathname)) { navigating = true; location.reload(); return; }
    await window.BuyonikShop?.refresh().catch(() => {});
  }
  async function clearBrowserCart(cart) {
    await cartRequest('cart/clear.js', {});
    const attributes = {...Object.fromEntries(Object.keys(cart.attributes || {}).map(key => [key, ''])), [revisionAttribute]: ''};
    taggedRevision = null;
    return cartRequest('cart/update.js', {note: '', attributes});
  }
  function schedule() {
    if (!active || paused || leaving) return;
    clearTimeout(timer);
    timer = setTimeout(() => flush().catch(showError), 500);
  }
  async function flush() {
    await ready;
    if (!active || paused) throw fail(paused ? 'conflict' : 'connect');
    await Promise.allSettled([...requests]);
    return serial(async () => {
      if (read(localStorage, 'owner')?.id !== cfg.customerId) throw fail('identity');
      await initializeAccount();
      if (paused) throw fail('conflict');
    });
  }
  function showError(error) {
    window.BuyonikCartTransition?.hide();
    if (!paused) setStatus(error.message || t.error, active && error.message !== t.connect ? 'retry' : 'connect', true);
  }
  function coveredBySavedCart(cart, saved) {
    const value = snapshot(cart);
    if (value.note && value.note !== saved?.note) return false;
    const quantities = new Map();
    for (const item of [...(saved?.items || []), ...(saved?.deferredItems || [])]) quantities.set(lineKey(item), (quantities.get(lineKey(item)) || 0) + item.quantity);
    return value.items.every(item => {
      const key = lineKey(item), remaining = (quantities.get(key) || 0) - item.quantity;
      quantities.set(key, remaining);
      return remaining >= 0;
    });
  }
  async function signOut(href = cfg.logoutUrl) {
    if (leaving) return;
    await ready;
    if (!active) { await authorize(true, 'logout'); return; }
    const pausedStatus = paused ? {...status} : null;
    leaving = true; clearTimeout(timer); setStatus(t.leaving);
    window.BuyonikCartTransition?.show(t.leaving);
    try {
      if (!paused) await flush();
      await Promise.allSettled([...requests]);
      const config = await discover(), token = auth.id_token;
      await serial(async () => {
        const cart = await cartRequest();
        // A mutation finishing during the last save is included before clearing.
        if (paused) {
          const remote = await remoteCart();
          if (!coveredBySavedCart(cart, remote.value)) throw fail('conflict');
        } else {
          await persist(cart);
        }
        await clearBrowserCart(cart);
        put(localStorage, 'restore:' + cfg.customerId, null);
      });
      active = false;
      put(sessionStorage, 'logout', {at: Date.now(), href});
      put(sessionStorage, 'auth', null); put(sessionStorage, 'attempted', null);
      put(localStorage, 'owner', null);
      const url = new URL(config.end_session_endpoint);
      url.searchParams.set('id_token_hint', token);
      url.searchParams.set('post_logout_redirect_uri', cfg.redirectUri);
      navigating = true; location.assign(url.href);
    } catch (error) {
      leaving = false;
      window.BuyonikCartTransition?.hide();
      if (paused) setStatus(pausedStatus?.message || t.conflict, pausedStatus?.action || 'keep', true);
      else setStatus(t.logout_error, 'logout', true);
    }
  }
  const isCartMutation = (url, method) => {
    try { const parsed = new URL(url, location.origin); return parsed.origin === location.origin && String(method).toUpperCase() !== 'GET' && /\/cart\/(add|change|update|clear)(\.js)?$/.test(parsed.pathname); }
    catch { return false; }
  };
  window.fetch = function(input, options) {
    const url = typeof input === 'string' || input instanceof URL ? String(input) : input.url;
    const method = options?.method || input?.method || 'GET';
    if (!isCartMutation(url, method)) return nativeFetch(input, options);
    const task = (async () => {
      await ready;
      if (leaving) throw fail('leaving');
      const response = await nativeFetch(input, options);
      schedule(); return response;
    })();
    requests.add(task); task.finally(() => requests.delete(task)).catch(() => {});
    return task;
  };
  // Older theme widgets use XHR; observe their completion without changing payloads.
  const xhrOpen = XMLHttpRequest.prototype.open, xhrSend = XMLHttpRequest.prototype.send;
  const cartXhrs = new WeakSet();
  XMLHttpRequest.prototype.open = function(method, url, ...args) {
    if (isCartMutation(url, method)) cartXhrs.add(this); else cartXhrs.delete(this);
    return xhrOpen.call(this, method, url, ...args);
  };
  XMLHttpRequest.prototype.send = function(...args) {
    if (cartXhrs.has(this)) {
      const xhr = this;
      const task = new Promise((resolve, reject) => {
        xhr.addEventListener('loadend', () => { schedule(); resolve(); }, {once: true});
        ready.then(() => {
          if (leaving) { xhr.abort(); reject(fail('leaving')); return; }
          try { xhrSend.apply(xhr, args); } catch (error) { reject(error); }
        });
      });
      requests.add(task); task.finally(() => requests.delete(task)).catch(() => {});
      return;
    }
    return xhrSend.apply(this, args);
  };
  document.addEventListener('click', event => {
    const action = event.target.closest('[data-by-saved-action]');
    if (action) {
      event.preventDefault();
      if (action.dataset.bySavedAction === 'connect') authorize(true).catch(showError);
      else if (action.dataset.bySavedAction === 'logout') signOut().catch(showError);
      else if (action.dataset.bySavedAction === 'retry-stock') retryStock().catch(showError);
      else if (action.dataset.bySavedAction === 'keep') serial(async () => {
        const remote = await remoteCart();
        await completedRevisions(remote, read(localStorage, 'owner'));
        digest = remote.digest; currentSaved = remote.value; deferredItems = remote.value?.deferredItems || []; savedFingerprint = '';
        paused = false; active = true;
        const cart = await cartRequest(); remember(cart); await persist(cart);
      }).catch(showError);
      else flush().catch(showError);
      return;
    }
    const link = event.target.closest('a[href]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.origin);
    const logoutUrl = new URL(cfg.logoutUrl, location.origin);
    if (link.matches('[data-buyonik-signout]') || (url.origin === logoutUrl.origin && url.pathname === logoutUrl.pathname) || (url.origin === location.origin && /\/account\/logout\/?$/.test(url.pathname))) {
      if (!cfg.customerId) return;
      event.preventDefault(); event.stopImmediatePropagation(); signOut(link.href).catch(showError);
    }
  }, true);
  window.addEventListener('storage', event => {
    if (event.key !== ownerKey) return;
    let owner; try { owner = event.newValue ? JSON.parse(event.newValue) : null; } catch { return; }
    if (active && owner?.id !== cfg.customerId) {
      active = false; leaving = true; put(sessionStorage, 'auth', null); location.reload();
    } else if (active && owner?.id === cfg.customerId) {
      // These tabs share one Shopify browser cart, and serialize saves with Web Locks.
      digest = owner.digest;
    }
  });
  window.addEventListener('online', schedule);
  window.addEventListener('focus', schedule);
  window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const path = new URL(form.action, location.origin).pathname;
    if (!/\/cart(?:\/|$)/.test(path)) return;
    if (approvedCheckoutForms.has(form)) { approvedCheckoutForms.delete(form); return; }
    if (event.submitter?.name === 'checkout' && cfg.customerId) {
      event.preventDefault(); event.stopImmediatePropagation();
      const submitter = event.submitter;
      flush().then(() => {
        approvedCheckoutForms.add(form);
        form.requestSubmit(submitter);
      }).catch(showError);
    } else if (initializing || leaving) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  async function start() {
    try {
      put(sessionStorage, 'probe', 1); put(sessionStorage, 'probe', null);
      put(localStorage, 'probe', 1); put(localStorage, 'probe', null);
    } catch { throw fail('storage'); }
    const logout = read(sessionStorage, 'logout');
    if (logout && Date.now() - logout.at < 600000) {
      put(sessionStorage, 'logout', null);
      if (cfg.customerId) { navigating = true; location.replace(cfg.logoutUrl); return; }
    }
    auth = read(sessionStorage, 'auth');
    if (!cfg.customerId) {
      put(sessionStorage, 'auth', null); put(sessionStorage, 'attempted', null);
      const owner = read(localStorage, 'owner');
      if (owner?.id) await serial(async () => {
        await clearBrowserCart(await cartRequest()); put(localStorage, 'owner', null);
      });
      setStatus(t.guest); return;
    }
    const pending = await callback();
    if (!auth || auth.customerId !== cfg.customerId) {
      if (read(sessionStorage, 'attempted') !== cfg.customerId) await authorize();
      else setStatus(t.connect, 'connect');
      return;
    }
    await serial(() => initializeAccount());
    if (pending?.intent === 'logout') { navigating = true; setTimeout(() => signOut().catch(showError), 0); }
    else if (pending?.returnTo && pending.returnTo !== targetPath()) { navigating = true; location.replace(targetPath(pending.returnTo)); }
    if (!navigating && cartRestored && /\/cart\/?$/.test(location.pathname)) { navigating = true; location.reload(); return; }
    if (!navigating) await window.BuyonikShop?.refresh().catch(() => {});
  }
  const ready = start().catch(showError).finally(() => {
    initializing = false;
    if (!navigating) window.BuyonikCartTransition?.hide();
  });
  window.BuyonikSavedCart = {flush, get state() { return {active, paused, initializing, message: status.message}; }};
  document.addEventListener('DOMContentLoaded', () => setStatus(status.message, status.action, status.loud));
})();


